import { useCallback, useEffect, useRef, useState } from 'react';
import ReplayIcon from '@mui/icons-material/Replay';
import { Alert, Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { Box } from '@mui/system';
import { useSnackbar } from 'notistack';
import { useLocation, useNavigate } from 'react-router-dom';
import { PDFJobRibbonProps } from './PDFJobRibbon.props';
import { ApiService, CompanyService, RetrieveDocumentGenerationJob } from '../../../../../api';
import { useStore } from '../../../../../common/store';
import { useAsyncProcessor } from '../../../../../context/AsyncProcessorContext/AsyncProcessorContext';
import { GenerationJobState } from '../../../../../pages/DocumentGenerationJobs/models/enums/generation-job-state.enum';
import { Logger } from '../../../../../utilities';

export const PDFJobRibbon = ({ generationJob, measurementDate, referenceObjectType = 'ct', onJobComplete, skipPolling, onRetrigger }: PDFJobRibbonProps) => {
  const { addJob, removeJob } = useAsyncProcessor();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const { apiCompanyDocumentGenerationJobsRead, apiCompanyDocumentGenerationJobsDocumentExecutionSessionsList } =
    ApiService;
  const [{ companyInfo }] = useStore();
  const [genJob, setGenJob] = useState(generationJob);
  const genJobRef = useRef(genJob);
  genJobRef.current = genJob;

  // Capture the location where the job was started
  const jobStartLocation = useRef({ pathname: location.pathname, search: location.search });

  const createFileReferencesFromJob = useCallback(async () => {
    const currentJob = genJobRef.current;
    try {
      if (!measurementDate?.id || !currentJob.id || !companyInfo.id) {
        return;
      }

      const page = undefined;
      const createFileReferences = true;

      await apiCompanyDocumentGenerationJobsDocumentExecutionSessionsList(
        companyInfo.id,
        `${currentJob.job_id}`,
        page,
        createFileReferences,
        measurementDate.id,
        referenceObjectType
      );
    } catch (error) {
      Logger.error('Error creating file references:', error);
      return [];
    }
  }, [
    apiCompanyDocumentGenerationJobsDocumentExecutionSessionsList,
    companyInfo,
    measurementDate?.id,
    referenceObjectType,
  ]);

  const handleResult = useCallback(
    async (result: RetrieveDocumentGenerationJob) => {
      const response = result;
      setGenJob(response);

      if (response.status !== RetrieveDocumentGenerationJob.status.COMPLETED) {
        return;
      }

      removeJob(response.job_id);

      try {
        await createFileReferencesFromJob();
        onJobComplete?.();

        enqueueSnackbar('Processing job completed', {
          variant: 'success',
          action: snackbarId => (
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                closeSnackbar(snackbarId);
                navigate(`${jobStartLocation.current.pathname}${jobStartLocation.current.search}`);
              }}>
              View
            </Button>
          ),
        });
      } catch (error) {
        Logger.error('Error creating file references:', error);
        enqueueSnackbar('Error creating file references from extraction results', { variant: 'error' });
      }
    },
    [removeJob, createFileReferencesFromJob, enqueueSnackbar, closeSnackbar, navigate, onJobComplete]
  );

  useEffect(() => {
    if (skipPolling) {
      setGenJob(generationJob);
    }
  }, [skipPolling, generationJob]);

  useEffect(() => {
    if (!generationJob.id || skipPolling) return;

    addJob({
      id: `${generationJob.job_id}`,
      details: {
        description: 'AI Assisted PDF Processing',
        title: 'PDF Processing',
      },
      pollingConfig: {
        pollingInterval: 3000,
        pollingArgs: [companyInfo.id, generationJob.job_id],
        pollingFunction: apiCompanyDocumentGenerationJobsRead,
      },
      onError: err => {
        Logger.error('Error on parsing', err);
        enqueueSnackbar('Processing job completed', { variant: 'error' });
      },
      onResult: handleResult,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generationJob, skipPolling]);

  const [retriggering, setRetriggering] = useState(false);

  const handleRetriggerParsing = useCallback(async () => {
    if (!companyInfo.id || !genJob.reference_files?.length) return;
    setRetriggering(true);
    try {
      const fileIds = genJob.reference_files.map((f: any) => f.id).filter(Boolean);
      if (!fileIds.length) {
        enqueueSnackbar('No reference files to reprocess', { variant: 'warning' });
        return;
      }

      const newJob = await CompanyService.apiCompanyDocumentGenerationJobsNewCreate(
        companyInfo.id.toString(),
        {
          reference_files: fileIds,
          processing_type: RetrieveDocumentGenerationJob.processing_type.PARSING,
          desired_termination_state: GenerationJobState.PENDING_PROMPTING,
          parser_overrides: genJob.parser_overrides ?? [],
          metadata: genJob.metadata ?? {},
        }
      );

      if (onRetrigger) {
        onRetrigger(newJob);
      } else {
        setGenJob(newJob);
        addJob({
          id: newJob.job_id,
          details: {
            description: 'AI Assisted PDF Processing',
            title: 'PDF Re-processing',
          },
          pollingConfig: {
            pollingInterval: 3000,
            pollingArgs: [companyInfo.id, newJob.job_id],
            pollingFunction: apiCompanyDocumentGenerationJobsRead,
          },
          onError: err => {
            Logger.error('Error on re-parsing', err);
            enqueueSnackbar('Re-processing job failed', { variant: 'error' });
          },
          onResult: handleResult,
        });
      }

      enqueueSnackbar('Re-processing job started', { variant: 'info' });
    } catch (error) {
      Logger.error('Error retriggering parsing job:', error);
      enqueueSnackbar('Failed to retrigger parsing', { variant: 'error' });
    } finally {
      setRetriggering(false);
    }
  }, [companyInfo.id, genJob, enqueueSnackbar, addJob, apiCompanyDocumentGenerationJobsRead, handleResult, onRetrigger]);

  const retriggerButton = (
    <Tooltip title="Re-run parsing">
      <IconButton size="small" onClick={handleRetriggerParsing} disabled={retriggering} sx={{ ml: 1 }}>
        {retriggering ? <CircularProgress size={16} /> : <ReplayIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );

  const renderJobStatusAlert = () => {
    const jobStatus = genJob.status;
    if (
      jobStatus === RetrieveDocumentGenerationJob.status.PENDING ||
      jobStatus === RetrieveDocumentGenerationJob.status.IN_PROGRESS
    ) {
      return (
        <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ display: 'flex', alignItems: 'center' }}>
          Processing document, You are free to navigate to other pages. Upon completion, you will be notified! Current
          Status: {jobStatus}
        </Alert>
      );
    }

    if (jobStatus === RetrieveDocumentGenerationJob.status.COMPLETED) {
      return (
        <Alert severity="success" action={retriggerButton}>
          Document processing completed successfully!
        </Alert>
      );
    }

    if (jobStatus === RetrieveDocumentGenerationJob.status.FAILED) {
      return (
        <Alert severity="error" action={retriggerButton}>
          Document processing failed. Please try again.
        </Alert>
      );
    }

    return null;
  };

  return <Box>{genJob && <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>{renderJobStatusAlert()}</Box>}</Box>;
};
